"use client";

import { FormEvent, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import styles from "./work.module.css";

type ContactPath = "message" | "booking";

const ServiceAnchor3D = lazy(() => import("./ServiceAnchor3D"));

type GlyphKind = "browser" | "nodes" | "broadcast" | "layers" | "radar" | "bars" | "candles" | "path";

function workImageSrcSet(src: string) {
  return `${src.replace(/\.webp$/, "-400.webp")} 400w, ${src.replace(/\.webp$/, "-720.webp")} 720w, ${src.replace(/\.webp$/, "-960.webp")} 960w, ${src} 1440w`;
}

const services = [
  {
    number: "01",
    slug: "websites",
    title: "Websites",
    copy: "Premium websites that translate a real business into a clear, fast, conversion-ready customer journey.",
    items: ["Positioning and information architecture", "Responsive interaction design", "Production build and launch"],
    glyph: "browser",
    group: "Anchor service",
    href: "#contact",
    action: "Start a website conversation",
    proof: { src: "/work/cg/01-hero.webp", label: "Website launch system · CG Walls & Floors" },
  },
  {
    number: "02",
    slug: "agents",
    title: "AI Agents",
    copy: "Human-controlled agents for research, drafting, routing, analysis, and repetitive operating work.",
    items: ["Workflow and exception design", "Tool and data connections", "Monitoring and safe handoff"],
    glyph: "nodes",
    group: "Anchor service",
    href: "#contact",
    action: "Start an agent conversation",
    proof: { src: "/work/cadencestack/01-pipeline.webp", label: "Workflow intelligence · CadenceStack" },
  },
  {
    number: "03",
    slug: "marketing",
    title: "Marketing & Brand Systems",
    copy: "Position, launch, and grow — the outward story before the inward system.",
    items: ["Brand positioning and voice", "Launch campaign architecture", "Cross-channel presence"],
    glyph: "broadcast",
    group: "Anchor service",
    href: "#contact",
    action: "Start a brand conversation",
    proof: { src: "/work/cg/04-testimonials.webp", label: "Brand-to-trust journey · CG Walls & Floors" },
  },
  {
    number: "04",
    slug: "saas",
    title: "SaaS Products",
    copy: "Focused software from first product logic through working interface, system behavior, and release readiness.",
    items: ["Product architecture", "AI-native features where useful", "Implementation and quality assurance"],
    glyph: "layers",
    group: "Proven in SIFT",
    href: "#work-top",
    action: "See the proof in SIFT",
    proof: { src: "/work/sift/01-overview.webp", label: "Product architecture · SIFT" },
  },
  {
    number: "05",
    slug: "outreach",
    title: "AI-Driven Lead Generation & Outreach",
    copy: "Automated systems that identify real opportunities and generate the first conversation, not just a form fill.",
    items: ["Signal identification and scraping", "Personalized outreach generation", "Pipeline and follow-up automation"],
    glyph: "radar",
    group: "Proven in CG Walls & Floors",
    href: "#cg-walls",
    action: "See the proof in CG Walls & Floors",
    proof: { src: "/work/cg/02-gallery.webp", label: "Conversion journey · CG Walls & Floors" },
  },
  {
    number: "06",
    slug: "growth",
    title: "Growth & Content Systems",
    copy: "Analytics-backed content operations that score, plan, and prove what's actually working.",
    items: ["Voice and performance scoring", "Content pillar strategy", "Analytics and reporting dashboards"],
    glyph: "bars",
    group: "Proven in CadenceStack",
    href: "#cadencestack",
    action: "See the proof in CadenceStack",
    proof: { src: "/work/cadencestack/02-analytics.webp", label: "Performance scoring · CadenceStack" },
  },
  {
    number: "07",
    slug: "finance",
    title: "Financial & Research Intelligence Platforms",
    copy: "Command-center-grade research tooling for teams that need signal, not noise.",
    items: ["Cross-market data integration", "Signal confidence scoring", "Simulated and educational environments"],
    glyph: "candles",
    group: "Proven in Capital Command",
    href: "#capital-command",
    action: "See the proof in Capital Command",
    proof: { src: "/work/capital-command/01-overview.webp", label: "Research command center · Capital Command" },
  },
  {
    number: "08",
    slug: "career",
    title: "Career & Talent Platforms",
    copy: "Job and application intelligence — ATS scoring, outreach, and pipeline tracking in one system.",
    items: ["ATS and resume analysis", "Contact discovery and outreach", "Application pipeline tracking"],
    glyph: "path",
    group: "Proven in SIFT",
    href: "#work-top",
    action: "See the proof in SIFT",
    proof: { src: "/work/sift/02-match-intelligence.webp", label: "Career intelligence · SIFT" },
  },
] as const;

const OBSERVE_QUOTE = "Posts feel random. Engagement is flat. No way to know what actually worked until three weeks later.";
const OBSERVE_QUESTION = "What if the post could be scored before it went out?";
const signalKeywords = ["broken", "slow", "stuck", "conversion", "revenue", "pipeline", "users", "leads", "sales", "traffic", "churn", "launch", "hours", "cost", "response", "retention"];

const serviceSignalPoints = [
  [14.5, 20], [85.5, 20], [49.5, 84], [8.5, 49],
  [91.5, 50], [20.5, 76], [79.5, 76], [49.5, 8],
] as const;

const serviceOrbitPositions = [
  { left: "7%", top: "13%" },
  { right: "7%", top: "13%" },
  { left: "42%", top: "77%" },
  { left: "1%", top: "42%" },
  { right: "1%", top: "43%" },
  { left: "13%", top: "69%" },
  { right: "13%", top: "69%" },
  { left: "42%", top: "1%" },
] as const;

function ServiceGlyph({ kind }: { kind: GlyphKind }) {
  const children = kind === "nodes" ? <><i /><i /><i /><b /><b /></>
    : kind === "broadcast" ? <><i /><i /><i /><b /></>
    : kind === "layers" || kind === "bars" ? <><i /><i /><i /></>
    : kind === "candles" ? <><i /><i /></>
    : kind === "radar" || kind === "path" ? <><i /><b /></>
    : <i />;
  return <span className={`${styles.serviceGlyph} ${styles[`glyph${kind[0].toUpperCase()}${kind.slice(1)}` as keyof typeof styles]}`} aria-hidden="true">{children}</span>;
}

function ServiceAnchor({ activeSlug, reducedMotion }: { activeSlug: string | null; reducedMotion: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const [webgl, setWebgl] = useState(false);
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
    const canvas = document.createElement("canvas");
    let context: WebGLRenderingContext | WebGL2RenderingContext | null = null;
    try {
      context = canvas.getContext("webgl2") || canvas.getContext("webgl");
    } catch {}
    const supported = Boolean(context);
    context?.getExtension("WEBGL_lose_context")?.loseContext();
    canvas.width = 0;
    canvas.height = 0;
      const frame = window.requestAnimationFrame(() => setWebgl(supported));
      return () => window.cancelAnimationFrame(frame);
    }, { rootMargin: "500px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={host} style={{ width: "100%", height: "100%" }}>
      {webgl ? <Suspense fallback={<div className={styles.serviceAnchorFallback}><i /><i /><i /><i /><i /></div>}><ServiceAnchor3D activeSlug={activeSlug} reducedMotion={reducedMotion} /></Suspense> : <div className={styles.serviceAnchorFallback}><i /><i /><i /><i /><i /></div>}
    </div>
  );
}

function ContactForm() {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState("");
  const words = message.trim() ? message.trim().split(/\s+/) : [];
  const keywordHits = signalKeywords.filter((word) => message.toLowerCase().includes(word)).length;
  const numberHits = message.match(/\b\d+(?:[.,]\d+)?%?\b/g)?.length ?? 0;
  const clarity = Math.min(100, Math.round((words.length / 42) * 100));
  const specificity = Math.min(100, Math.round(keywordHits * 14 + numberHits * 18 + Math.min(words.length, 30) * .7));
  const strength = Math.round((clarity + specificity) / 2);
  const hint = words.length === 0 ? "Start typing."
    : words.length < 8 ? "Keep going — I need more to work with."
    : specificity < 38 ? "What’s the specific number or symptom?"
    : strength < 72 ? "Good shape. One measurable outcome?"
    : "Strong signal. This is workable.";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("sending");
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    try {
      const response = await fetch("/api/contact", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error("Request failed");
      form.reset();
      setMessage("");
      setState("sent");
    } catch {
      setState("error");
    }
  };
  if (state === "sent") return <div className={styles.contactSuccess} role="status"><i>✓</i><strong>Thank you — your message is in.</strong><span>I&apos;ll review the details and get back to you shortly.</span></div>;
  return (
    <div className={styles.messageDrawerGrid}>
      <form className={styles.contactForm} onSubmit={submit}>
        <div className={styles.contactFormSplit}>
          <label><span>Name</span><input name="name" autoComplete="name" required /></label>
          <label><span>Email</span><input name="email" type="email" autoComplete="email" required /></label>
        </div>
        <div className={styles.contactFormSplit}>
          <label><span>Phone</span><input name="phone" type="tel" autoComplete="tel" /></label>
          <label><span>Looking for</span><select name="interest" defaultValue="" required><option value="" disabled>Choose one</option><option>Website</option><option>Marketing system</option><option>AI agent</option><option>SaaS product</option><option>Something else</option></select></label>
        </div>
        <label><span>What&apos;s the actual problem?</span><textarea name="message" rows={5} value={message} onChange={(event) => { setMessage(event.target.value); if (state !== "idle") setState("idle"); }} aria-describedby="signal-analyzer" required /></label>
        <aside id="signal-analyzer" className={styles.signalAnalyzer} aria-label="Live signal analyzer" aria-live="polite">
          <p>Signal analyzer</p>
          {[["Clarity", clarity], ["Specificity", specificity], ["Signal strength", strength]].map(([label, score]) => (
            <div className={styles.signalMetric} key={label}>
              <span><em>{label}</em><strong>{score}</strong></span>
              <i><b style={{ width: `${score}%` }} /></i>
            </div>
          ))}
          <small>{hint}</small>
        </aside>
        <button type="submit" disabled={state === "sending"}>{state === "sending" ? "Sending…" : state === "sent" ? "Signal received" : "Send signal"}<i aria-hidden="true">↗</i></button>
        <small className={styles.privacyNotice}>By submitting, you agree to our <a href="/privacy">Privacy Policy</a>.</small>
        <output aria-live="polite">{state === "error" ? "The signal could not be sent. Please try again." : state === "sent" ? "Thanks — your message is safely recorded." : ""}</output>
      </form>
    </div>
  );
}

function LeadCaptureNote({ onClose, onBook }: { onClose: (outcome: "dismissed" | "submitted") => void; onBook: () => void }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("sending");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const response = await fetch("/api/contact", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error("Request failed");
      setState("sent");
      window.setTimeout(() => onClose("submitted"), 2200);
    } catch {
      setState("error");
    }
  };
  return (
    <aside className={styles.leadNote} role="dialog" aria-modal="false" aria-labelledby="lead-note-title">
      <button className={styles.leadNoteClose} type="button" onClick={() => onClose("dismissed")} aria-label="Close project note">×</button>
      <p>Before you go further</p>
      <h2 id="lead-note-title">What are you trying to build?</h2>
      <span>Share the details below, or book a meeting if you would rather talk it through.</span>
      {state === "sent" ? <div className={styles.leadNoteSuccess}><i>✓</i><strong>Signal received.</strong><small>I&apos;ll review it and get back to you.</small></div> : (
        <>
          <form onSubmit={submit}>
            <div><label><span>Name</span><input name="name" autoComplete="name" required /></label><label><span>Phone</span><input name="phone" type="tel" autoComplete="tel" required /></label></div>
            <label><span>Email</span><input name="email" type="email" autoComplete="email" required /></label>
            <label><span>Looking for</span><select name="interest" defaultValue="" required><option value="" disabled>Choose a project type</option><option>Website</option><option>Marketing system</option><option>AI agent</option><option>SaaS product</option><option>Something else</option></select></label>
            <label><span>What should work better?</span><textarea name="message" rows={3} required /></label>
            <button type="submit" disabled={state === "sending"}>{state === "sending" ? "Sending…" : "Send message"}<i>↗</i></button>
            <small className={styles.privacyNotice}>By submitting, you agree to our <a href="/privacy">Privacy Policy</a>.</small>
            <output aria-live="polite">{state === "error" ? "Couldn’t send that yet. Please try again." : ""}</output>
          </form>
          <div className={styles.leadNoteBooking}>
            <span>Prefer to choose a time directly?</span>
            <button type="button" onClick={onBook}><strong>Book a meeting</strong><i aria-hidden="true">↗</i></button>
          </div>
        </>
      )}
    </aside>
  );
}

function BookingDrawer() {
  return (
    <div className={styles.bookingDrawer}>
      <div className={styles.bookingExternal}>
        <small>30 minutes · Google Meet</small>
        <strong>Discovery Call with Averr Studios</strong>
        <p>Choose a live time directly on Cal.com. Your name, email, and booking details are collected securely during scheduling.</p>
        <a href="https://cal.com/prachets/discoverycall" target="_blank" rel="noopener noreferrer">
          View live availability
          <span aria-hidden="true">↗</span>
        </a>
        <em>Opens Cal.com in a new tab.</em>
      </div>
    </div>
  );
}

function ReducedMotionProcess() {
  const frameworkNodes = ["Voice", "Strength", "Hook", "Cadence", "Signal", "Score", "Recommendation", "Publish Decision"];
  const metrics = [["Idea Bank", "2"], ["Validated", "1"], ["Drafting", "5"], ["Ready to Post", "1"], ["Voice", "8"], ["Strength", "8"]];

  return (
    <section id="process" className={styles.processEditorial} aria-labelledby="process-title">
      <header className={styles.processEditorialHeader}>
        <p>06 · HOW THE WORK MOVES</p>
        <h2 id="process-title">Watch the work become a system.</h2>
        <span>Four complete views of the same process, presented without motion.</span>
      </header>

      <div className={styles.processEditorialSlides}>
        <article className={styles.processEditorialSlide}>
          <header><span>01 / 04</span><strong>Observe</strong></header>
          <div className={`${styles.processEditorialFrame} ${styles.processEditorialObserve}`}>
            <small>Distilled question</small>
            <h3>{OBSERVE_QUESTION}</h3>
          </div>
          <p>One useful question replaces a cloud of symptoms—and gives the build a testable direction.</p>
        </article>

        <article className={styles.processEditorialSlide}>
          <header><span>02 / 04</span><strong>Structure</strong></header>
          <div className={`${styles.processEditorialFrame} ${styles.processEditorialStructure}`}>
            <small>Structure · CadenceStack</small>
            <div className={styles.processEditorialPillars}>
              {frameworkNodes.map((node, index) => <div key={node} data-output={index >= 5}><span>{index < 5 ? "Content pillar" : index === 7 ? "Decision" : "System output"}</span><strong>{node}</strong></div>)}
            </div>
            <div className={styles.processEditorialDependencies} aria-label="Voice and Strength produce Score; Score and Cadence produce Recommendation; Recommendation and Signal produce Publish Decision">
              <div><span>Voice + Strength</span><i>→</i><strong>Score</strong></div>
              <div><span>Score + Cadence</span><i>→</i><strong>Recommendation</strong></div>
              <div><span>Recommendation + Signal</span><i>→</i><strong>Publish Decision</strong></div>
            </div>
          </div>
          <p>Five content pillars become a dependency system: evidence is scored, recommendations are formed, and a publish decision becomes explicit.</p>
        </article>

        <article className={styles.processEditorialSlide}>
          <header><span>03 / 04</span><strong>Build</strong></header>
          <figure className={`${styles.processEditorialFrame} ${styles.processEditorialScreen}`}>
            <img src="/work/cadencestack/01-pipeline.webp" srcSet={workImageSrcSet("/work/cadencestack/01-pipeline.webp")} sizes="(max-width: 760px) 92vw, 70vw" alt="The completed CadenceStack Content Pipeline dashboard" width="2048" height="1080" loading="lazy" decoding="async" />
          </figure>
          <p>The framework resolves into the real CadenceStack Pipeline: navigation, production stages, scored post cards, recommendations, and publish readiness.</p>
        </article>

        <article className={styles.processEditorialSlide}>
          <header><span>04 / 04</span><strong>Refine</strong></header>
          <div className={`${styles.processEditorialFrame} ${styles.processEditorialRefine}`}>
            <img src="/work/cadencestack/01-pipeline.webp" srcSet={workImageSrcSet("/work/cadencestack/01-pipeline.webp")} sizes="(max-width: 760px) 92vw, 70vw" alt="The finished and shipped CadenceStack Content Pipeline" width="2048" height="1080" loading="lazy" decoding="async" />
            <div className={styles.processEditorialMetrics}>{metrics.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
            <div className={styles.processEditorialShipped}><i aria-hidden="true">✓</i><span><strong>Shipped</strong><small>Measured · Iterating</small></span></div>
          </div>
          <p className={styles.processEditorialCaption}>This is how the CadenceStack platform actually got built.</p>
        </article>
      </div>
    </section>
  );
}

function ProcessObserveFilm({ compact = false }: { compact?: boolean }) {
  const sequenceRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const section = sequenceRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, section.offsetHeight - window.innerHeight);
      setProgress(Math.max(0, Math.min(1, -rect.top / travel)));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const effectiveProgress = compact ? (progress >= .75 ? .97 : progress >= .5 ? .72 : progress >= .25 ? .47 : .22) : progress;
  const observeProgress = Math.max(0, Math.min(1, effectiveProgress / .25));
  const structureProgress = Math.max(0, Math.min(1, (effectiveProgress - .25) / .25));
  const buildProgress = Math.max(0, Math.min(1, (effectiveProgress - .5) / .25));
  const refineProgress = Math.max(0, Math.min(1, (effectiveProgress - .75) / .25));
  const activeMovement = effectiveProgress >= .75 ? 4 : effectiveProgress >= .5 ? 3 : effectiveProgress >= .25 ? 2 : 1;
  const typedProgress = Math.min(1, observeProgress / .58);
  const typedLength = Math.round(OBSERVE_QUOTE.length * typedProgress);
  const resolved = Math.max(0, Math.min(1, (observeProgress - .66) / .24));
  const noteProgress = Math.max(0, Math.min(1, (observeProgress - .26) / .34));
  const structureVisibility = Math.max(0, Math.min(1, structureProgress / .16));
  const structureQuestion = Math.max(0, 1 - Math.max(0, Math.min(1, structureProgress / .28)));
  const pillarProgress = [0, 1, 2, 3, 4].map((index) => Math.max(0, Math.min(1, (structureProgress - (.13 + index * .07)) / .16)));
  const outputProgress = [0, 1, 2].map((index) => Math.max(0, Math.min(1, (structureProgress - (.48 + index * .13)) / .14)));
  const connectionProgress = [0, 1, 2, 3, 4, 5].map((index) => Math.max(0, Math.min(1, (structureProgress - (.37 + index * .065)) / .24)));
  const structureExit = Math.max(0, Math.min(1, buildProgress / .18));
  const buildVisibility = Math.max(0, Math.min(1, buildProgress / .12));
  const buildSidebar = Math.max(0, Math.min(1, (buildProgress - .04) / .18));
  const buildMetrics = Math.max(0, Math.min(1, (buildProgress - .17) / .2));
  const buildBoard = Math.max(0, Math.min(1, (buildProgress - .32) / .22));
  const buildPosts = Math.max(0, Math.min(1, (buildProgress - .49) / .22));
  const buildDetail = Math.max(0, Math.min(1, (buildProgress - .65) / .2));
  const buildFill = Math.max(0, Math.min(1, (buildProgress - .76) / .2));
  const refineVisibility = Math.max(0, Math.min(1, refineProgress / .12));
  const metricValue = (target: number, index: number) => {
    const start = .12 + index * .065;
    const local = Math.max(0, Math.min(1, (refineProgress - start) / .25));
    const eased = 1 - Math.pow(1 - local, 3);
    return Math.round(target * eased);
  };
  const refineMetrics = [
    ["Idea Bank", metricValue(2, 0)],
    ["Validated", metricValue(1, 1)],
    ["Drafting", metricValue(5, 2)],
    ["Ready to Post", metricValue(1, 3)],
    ["Voice", metricValue(8, 4)],
    ["Strength", metricValue(8, 5)],
  ] as const;
  const shippedProgress = Math.max(0, Math.min(1, (refineProgress - .7) / .1));
  const captionProgress = Math.max(0, Math.min(1, (refineProgress - .8) / .08));

  return (
    <section
      ref={sequenceRef}
      id="process"
      className={styles.processFilmSequence}
      aria-labelledby="process-title"
      style={{
        "--film-progress": progress.toFixed(4),
        "--observe-progress": observeProgress.toFixed(4),
        "--note-progress": noteProgress.toFixed(4),
        "--resolve-progress": resolved.toFixed(4),
        "--structure-progress": structureProgress.toFixed(4),
        "--structure-visible": structureVisibility.toFixed(4),
        "--structure-question": structureQuestion.toFixed(4),
        "--structure-exit": structureExit.toFixed(4),
        "--build-progress": buildProgress.toFixed(4),
        "--build-visible": buildVisibility.toFixed(4),
        "--build-sidebar": buildSidebar.toFixed(4),
        "--build-metrics": buildMetrics.toFixed(4),
        "--build-board": buildBoard.toFixed(4),
        "--build-posts": buildPosts.toFixed(4),
        "--build-detail": buildDetail.toFixed(4),
        "--build-fill": buildFill.toFixed(4),
        "--refine-progress": refineProgress.toFixed(4),
        "--refine-visible": refineVisibility.toFixed(4),
        "--shipped-progress": shippedProgress.toFixed(4),
        "--caption-progress": captionProgress.toFixed(4),
      } as React.CSSProperties}
    >
      <div className={styles.processFilmPinned}>
        <header className={styles.processFilmHeader}>
          <p>06 · HOW THE WORK MOVES</p>
          <h2 id="process-title">Watch the work become a system.</h2>
          <div className={styles.processMovementIndex}><span>0{activeMovement} / 04</span><strong>{activeMovement === 1 ? "Observe" : activeMovement === 2 ? "Structure" : activeMovement === 3 ? "Build" : "Refine"}</strong></div>
        </header>

        <div className={styles.observeStage} aria-live="polite">
          <div className={styles.observeWorkbench} aria-hidden="true"><i /><i /><i /></div>
          <div className={styles.observeQuote}>
            <span className={styles.observeLabel}>Raw friction · CadenceStack</span>
            <blockquote>
              <span>{OBSERVE_QUOTE.slice(0, typedLength)}</span>
              {typedLength < OBSERVE_QUOTE.length && <i className={styles.observeCaret} />}
            </blockquote>
            <svg className={styles.observeAnnotations} viewBox="0 0 900 330" preserveAspectRatio="none" aria-hidden="true">
              <path className={styles.observeUnderline} pathLength="1" d="M126 112 C180 123 245 104 306 114" />
              <path className={styles.observeCircle} pathLength="1" d="M318 87 C440 53 600 71 626 124 C651 181 547 211 421 194 C317 180 278 119 318 87 Z" />
              <path className={styles.observeQuestionMark} pathLength="1" d="M774 177 C822 133 867 172 841 210 C823 235 800 230 800 260 M800 291 L801 294" />
            </svg>
          </div>

          <div className={styles.observeResolution}>
            <span>Distilled question</span>
            <h3>{OBSERVE_QUESTION}</h3>
            <p>One useful question replaces a cloud of symptoms—and gives the build a testable direction.</p>
          </div>

          <div className={styles.structureStage} aria-label="CadenceStack content scoring framework">
            <p className={styles.structureSourceQuestion}>{OBSERVE_QUESTION}</p>
            <div className={styles.structureFramework}>
              <svg className={styles.structureConnections} viewBox="0 0 1000 520" preserveAspectRatio="none" aria-hidden="true">
                {[
                  "M130 104 C130 172 190 176 235 245",
                  "M330 104 C330 172 285 180 255 245",
                  "M255 278 C310 332 385 330 470 360",
                  "M530 104 C530 220 505 274 495 350",
                  "M500 392 C595 418 680 430 785 451",
                  "M730 104 C730 264 760 340 790 441",
                ].map((path, index) => (
                  <path key={path} pathLength="1" d={path} style={{ "--connection-progress": connectionProgress[index].toFixed(4) } as React.CSSProperties} />
                ))}
              </svg>

              {[
                ["Voice", "voice"], ["Strength", "strength"], ["Hook", "hook"], ["Cadence", "cadence"], ["Signal", "signal"],
              ].map(([label, position], index) => (
                <div key={label} className={`${styles.structureNode} ${styles[`structure${position[0].toUpperCase()}${position.slice(1)}` as keyof typeof styles]}`} style={{ "--node-progress": pillarProgress[index].toFixed(4) } as React.CSSProperties}>
                  <span>Content pillar</span><strong>{label}</strong>
                </div>
              ))}

              {[
                ["Score", "score"], ["Recommendation", "recommendation"], ["Publish Decision", "publish"],
              ].map(([label, position], index) => (
                <div key={label} className={`${styles.structureNode} ${styles.structureOutput} ${styles[`structure${position[0].toUpperCase()}${position.slice(1)}` as keyof typeof styles]}`} style={{ "--node-progress": outputProgress[index].toFixed(4) } as React.CSSProperties}>
                  <span>{index === 2 ? "Decision" : "System output"}</span><strong>{label}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.buildStage} aria-label="CadenceStack Content Pipeline assembling from wireframe into the real interface">
            <div className={styles.buildPipelineShell}>
              <p className={styles.buildEyebrow}>Build · CadenceStack</p>

              <aside className={styles.buildSidebar}>
                <div className={styles.buildBrand}><i aria-hidden="true">≋</i><span><strong>CadenceStack</strong><small>Content operating system</small></span></div>
                <span>Operate</span>
                <nav aria-label="CadenceStack Pipeline navigation">
                  {['Command Center', 'Content Pipeline', 'Post Database', 'Content Calendar'].map((item) => <b key={item} data-active={item === 'Content Pipeline'}>{item}</b>)}
                </nav>
                <span>Create</span>
                <nav>
                  {['Idea Bank', 'Content Pillars', 'Templates', 'Prompt Library'].map((item) => <b key={item}>{item}</b>)}
                </nav>
              </aside>

              <div className={styles.buildMain}>
                <header className={styles.buildScreenHeader}>
                  <div><strong>Content Pipeline</strong><span>Drag posts through the production system</span></div>
                  <button type="button" tabIndex={-1}>＋ New Post</button>
                </header>

                <div className={styles.buildMetricRow}>
                  {[['Idea Bank', '2'], ['Validated Idea', '1'], ['Drafting', '5'], ['Ready to Post', '1']].map(([label, value]) => (
                    <div key={label}><span>{label}</span><strong>{value}</strong></div>
                  ))}
                </div>

                <div className={styles.buildBoard}>
                  <div className={styles.buildLane}>
                    <span>Idea Bank</span>
                    <article><strong>More activity does not fix a broken revenue system</strong><small>Sales Architecture</small><footer><b>Voice <i>7</i></b><b>Strength <i>7</i></b></footer></article>
                    <article><strong>The deal did not die at closing</strong><small>Deal Diagnosis</small></article>
                  </div>
                  <div className={styles.buildLane}>
                    <span>Validated Idea</span>
                    <article><strong>Sales is a design problem disguised as a numbers game</strong><small>Sales Architecture</small><footer><b>Voice <i>7</i></b><b>Strength <i>7</i></b></footer></article>
                  </div>
                  <div className={styles.buildLane}>
                    <span>Drafting</span>
                    <article><strong>Phase 2_Test_LinkedIn_Post</strong><small>Sales Architecture</small><footer><b>Voice <i>8</i></b><b>Strength <i>8</i></b></footer></article>
                    <article><strong>A full {"{{thing}}"} is not proof your {"{{system}}"} works.</strong><small>Sales Architecture</small></article>
                  </div>
                </div>

                <aside className={styles.buildScoringDetail}>
                  <span>System output</span><strong>Score</strong><div><b>Voice <i>8</i></b><b>Strength <i>8</i></b></div>
                  <span>Recommendation</span><p>Visual approved</p>
                  <span>Publish decision</span><em>Ready to Post</em>
                </aside>
              </div>

              <img className={styles.buildRealScreen} src="/work/cadencestack/01-pipeline.webp" srcSet={workImageSrcSet("/work/cadencestack/01-pipeline.webp")} sizes="(max-width: 760px) 92vw, 70vw" alt="The real CadenceStack Content Pipeline interface" width="2048" height="1080" loading="lazy" decoding="async" />
            </div>
          </div>

          <div className={styles.refineStage} aria-label="The live CadenceStack Pipeline measured and shipped">
            <div className={styles.refineComposition}>
              <img src="/work/cadencestack/01-pipeline.webp" srcSet={workImageSrcSet("/work/cadencestack/01-pipeline.webp")} sizes="(max-width: 760px) 92vw, 70vw" alt="The finished CadenceStack Content Pipeline" width="2048" height="1080" loading="lazy" decoding="async" />
              <div className={styles.refineMetricRail} aria-label="Real CadenceStack Pipeline measures">
                {refineMetrics.map(([label, value]) => (
                  <div key={label}><span>{label}</span><strong>{value}</strong></div>
                ))}
              </div>
              <div className={styles.refineShipped}><i aria-hidden="true">✓</i><span><strong>Shipped</strong><small>Measured · Iterating</small></span></div>
            </div>
            <p className={styles.refineCaption}>This is how the CadenceStack platform actually got built.</p>
          </div>
        </div>

        <div className={styles.processFilmProgress} aria-hidden="true"><span /><i className={styles.processDotActive} /><i className={activeMovement >= 2 ? styles.processDotActive : ""} /><i className={activeMovement >= 3 ? styles.processDotActive : ""} /><i className={activeMovement >= 4 ? styles.processDotActive : ""} /></div>
      </div>
    </section>
  );
}

export default function PostWork() {
  const [floatOpen, setFloatOpen] = useState(false);
  const [leadNoteOpen, setLeadNoteOpen] = useState(false);
  const [founderVisible, setFounderVisible] = useState(false);
  const [servicesVisible, setServicesVisible] = useState(false);
  const [activeService, setActiveService] = useState<string | null>(null);
  const founderRef = useRef<HTMLElement>(null);
  const founderStageRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [compactProcess, setCompactProcess] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches || new URLSearchParams(window.location.search).get("motion") === "reduce");
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 620px)");
    const update = () => setCompactProcess(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const selectService = useCallback((slug: string | null) => {
    setActiveService(slug);
    const nextHash = slug ? `#services/${slug}` : "#services";
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
  }, []);

  const openContact = (path: ContactPath) => {
    setLeadNoteOpen(false);
    window.requestAnimationFrame(() => document.getElementById(`contact-${path}`)?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "center" }));
  };

  useEffect(() => {
    const leadNoteKey = "averr-lead-note-v2";
    try { if (window.sessionStorage.getItem(leadNoteKey)) return; } catch {}
    let queued = false;
    const offerNote = () => {
      const contactTop = document.getElementById("contact")?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      if (queued || window.scrollY < window.innerHeight * 1.15 || contactTop < window.innerHeight) return;
      queued = true;
      window.setTimeout(() => setLeadNoteOpen(true), 280);
      window.removeEventListener("scroll", offerNote);
    };
    window.addEventListener("scroll", offerNote, { passive: true });
    offerNote();
    return () => window.removeEventListener("scroll", offerNote);
  }, []);

  useEffect(() => {
    if (!leadNoteOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setLeadNoteOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [leadNoteOpen]);

  const closeLeadNote = (outcome: "dismissed" | "submitted") => {
    try { window.sessionStorage.setItem("averr-lead-note-v2", outcome); } catch {}
    setLeadNoteOpen(false);
  };

  const chooseLeadPath = (path: "message" | "booking") => {
    try { window.sessionStorage.setItem("averr-lead-note-v2", path); } catch {}
    openContact(path);
  };

  useEffect(() => {
    if (!activeService) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") selectService(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [activeService, selectService]);

  useEffect(() => {
    const slug = window.location.hash.match(/^#services\/(.+)$/)?.[1];
    if (!slug || !services.some((service) => service.slug === slug)) return;
    const frame = window.requestAnimationFrame(() => {
      setActiveService(slug);
      servicesRef.current?.scrollIntoView({ block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const updateServiceMagnets = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reducedMotion || activeService) return;
    event.currentTarget.querySelectorAll<HTMLElement>("article").forEach((node) => {
      const rect = node.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const distance = Math.hypot(dx, dy);
      const strength = Math.max(0, 1 - distance / 240);
      node.style.setProperty("--orbit-mx", `${Math.max(-8, Math.min(8, dx * strength * .055)).toFixed(2)}px`);
      node.style.setProperty("--orbit-my", `${Math.max(-8, Math.min(8, dy * strength * .055)).toFixed(2)}px`);
    });
  };

  const resetServiceMagnets = (node: HTMLDivElement) => node.querySelectorAll<HTMLElement>("article").forEach((item) => {
    item.style.setProperty("--orbit-mx", "0px");
    item.style.setProperty("--orbit-my", "0px");
  });

  useEffect(() => {
    if (reducedMotion) {
      founderStageRef.current?.style.setProperty("--founder-y", "0px");
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const node = founderStageRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const centerDelta = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      const drift = Math.max(-6, Math.min(6, centerDelta * -8));
      node.style.setProperty("--founder-y", `${drift.toFixed(2)}px`);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (frame) window.cancelAnimationFrame(frame); };
  }, [reducedMotion]);

  useEffect(() => {
    const node = founderRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setFounderVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.22 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const targets = [
      { node: servicesRef.current, show: setServicesVisible },
    ];
    const observers = targets.flatMap(({ node, show }) => {
      if (!node) return [];
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          show(true);
          observer.disconnect();
        }
      }, { threshold: 0.16 });
      observer.observe(node);
      return [observer];
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  return (
    <>
      <section ref={servicesRef} id="services" className={`${styles.servicesSection} ${servicesVisible ? styles.servicesVisible : ""}`} aria-labelledby="services-title">
        <header className={styles.servicesHeader}>
          <p>SERVICES · WHAT AVERR STUDIOS BUILDS</p>
          <h2 id="services-title">What are you trying to build?</h2>
          <span>Websites, AI agents, SaaS products, marketing, and specialist systems — pointed at your business.</span>
        </header>

        <div
          className={`${styles.serviceWorkbench} ${activeService ? styles.orbitalActive : ""}`}
          onPointerMove={updateServiceMagnets}
          onPointerLeave={(event) => resetServiceMagnets(event.currentTarget)}
          onClick={(event) => {
            if (!(event.target as HTMLElement).closest(`.${styles.serviceOrbitNode},aside`)) selectService(null);
          }}
        >
          <div className={styles.serviceWorkbenchSurface} aria-hidden="true"><i /><i /></div>
          <svg className={styles.serviceSignals} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {serviceSignalPoints.map(([x, y], index) => (
              <line
                key={`${x}-${y}`}
                x1={x}
                y1={y}
                x2="50"
                y2="50"
                pathLength="1"
                className={activeService === services[index].slug ? styles.serviceSignalActive : ""}
              />
            ))}
            <circle cx="50" cy="50" r=".72" className={activeService ? styles.serviceSignalCoreActive : ""} />
          </svg>
          <div className={styles.serviceAnchorCanvas} aria-hidden="true">
            <ServiceAnchor activeSlug={activeService} reducedMotion={reducedMotion} />
            <span>{activeService ? `${services.find((service) => service.slug === activeService)?.title} instrument` : "Averr Studios instrument"}</span>
          </div>

          {services.map((service, index) => (
            <div className={styles.serviceMobileUnit} key={service.slug}>
            <button
              type="button"
              className={`${styles.serviceOrbitNode} ${index < 3 ? styles.serviceAnchorNode : ""} ${activeService === service.slug ? styles.serviceNodeActive : ""} ${activeService && activeService !== service.slug ? styles.serviceNodeReceded : ""}`}
              data-tone={index % 2 ? "light" : "dark"}
              style={{ "--orbit-index": index, ...serviceOrbitPositions[index] } as React.CSSProperties}
              aria-expanded={activeService === service.slug}
              aria-controls={`service-dossier-${service.slug}`}
              onClick={(event) => { event.stopPropagation(); selectService(activeService === service.slug ? null : service.slug); }}
            >
              <ServiceGlyph kind={service.glyph} />
              <span>{service.number} · {service.group}</span>
              <h3>{service.title}</h3>
            </button>
          {activeService === service.slug && (
            <aside id={`service-dossier-${service.slug}`} className={styles.orbitalDossier} aria-live="polite" onClick={(event) => event.stopPropagation()}>
              <button type="button" className={styles.orbitalClose} onClick={() => selectService(null)} aria-label={`Close ${service.title} dossier`}>×</button>
              <p>{service.number} · {service.group}</p>
              <h3>{service.title}</h3>
              <strong>{service.copy}</strong>
              <ul>{service.items.map((item) => <li key={item}>{item}</li>)}</ul>
              <figure className={styles.orbitalProof}>
                <div><img src={service.proof.src} srcSet={workImageSrcSet(service.proof.src)} sizes="(max-width: 760px) 84vw, 34vw" alt="" width="1440" height="797" loading="lazy" decoding="async" /><i aria-hidden="true" /></div>
                <figcaption><span>Live evidence</span>{service.proof.label}</figcaption>
              </figure>
              <a href={service.href}>{service.action}<span aria-hidden="true">↗</span></a>
            </aside>
          )}
          </div>
          ))}
        </div>
      </section>

      {reducedMotion ? <ReducedMotionProcess /> : <ProcessObserveFilm compact={compactProcess} />}

      <section ref={founderRef} id="founder" className={`${styles.founderSection} ${founderVisible ? styles.founderVisible : ""} ${reducedMotion ? styles.motionReduced : ""}`} aria-labelledby="founder-title">
        <div ref={founderStageRef} className={styles.founderStage}>
          <div className={styles.founderPhotoMount}><img src="/prachets-founder.webp" srcSet="/prachets-founder-320.webp 320w, /prachets-founder.webp 640w" sizes="(max-width: 760px) 72vw, 36vw" alt="Prachets Upadhyay" width="640" height="640" loading="lazy" decoding="async" /></div>
        </div>
        <article className={styles.founderCopy}>
          <p className={styles.founderEyebrow}>07 · A NOTE FROM THE FOUNDER</p>
          <div id="founder-title" className={styles.founderNote}>
            <p>Everything above this — I built it.</p>
            <p>SIFT. CG Walls &amp; Floors. CadenceStack. Capital Command. And this site.</p>
            <p>Not with a team. With a stack of AI agents I&apos;ve trained to work the way I do, in the hours around a full-time job at Google.</p>
            <p>Every one of them started the same way: a real problem, a person who needed it solved, and no obvious path to shipping it. That&apos;s the only kind of work I take on.</p>
            <p>If something in your business feels stuck like that — the thing you keep meaning to fix but haven&apos;t found the right person for — I&apos;d like to hear about it.</p>
          </div>
          <div className={styles.founderSignature}>
            <img src="/pu-founder-mark-ink.png" alt="PU monogram" width="260" height="400" loading="lazy" decoding="async" />
            <span><strong>Prachets Upadhyay</strong><small>Founder, Averr Studios · Account manager at Google</small></span>
          </div>
          <div className={styles.currentlyBuilding}><i aria-hidden="true" /><span><small>Currently building:</small> CadenceStack v2 · agent orchestration layer</span></div>
          <a className={styles.founderLinkedIn} href="https://www.linkedin.com/in/prachetsupadhyay" target="_blank" rel="noreferrer">Connect on LinkedIn <span aria-hidden="true">→</span></a>
        </article>
      </section>

      <section id="contact" className={`${styles.contactSection} ${reducedMotion ? styles.motionReduced : ""}`} aria-labelledby="contact-title">
        <div className={styles.contactGlow} aria-hidden="true" />
        <header><p>08 · LET&apos;S TALK</p><h2 id="contact-title">What needs to<br />work better?</h2><span>Choose how you&apos;d like to start the conversation.</span></header>
        <div className={styles.contactConduit} aria-hidden="true"><i /></div>
        <div className={styles.contactConsole}>
          <div className={styles.contactWorkspace}>
            <section id="contact-message" className={styles.contactWorkspacePanel} aria-labelledby="contact-message-title">
              <header><small>01 · WRITE IT DOWN</small><h3 id="contact-message-title">Send a message</h3><p>Tell me what needs to work better.</p></header>
              <ContactForm />
            </section>
            <section id="contact-booking" className={styles.contactWorkspacePanel} aria-labelledby="contact-booking-title">
              <header><small>02 · PUT TIME ON IT</small><h3 id="contact-booking-title">Schedule a call</h3><p>Choose a live 30-minute slot on Cal.com.</p></header>
              <BookingDrawer />
            </section>
          </div>
        </div>
      </section>

      <footer className={styles.workFooter}>
        <div><strong>AVERR <span>STUDIOS</span></strong><p>Websites, marketing systems, AI agents, and SaaS products</p></div>
        <nav aria-label="Footer navigation"><a href="#work-top">Work</a><a href="#services">Services</a><a href="#founder">Founder</a></nav>
        <div className={styles.footerContact}><a href="mailto:projects@averrstudios.com">projects@averrstudios.com</a><a href="tel:+16477432496">+1 647 743 2496</a><a href="https://www.linkedin.com/in/prachetsupadhyay" target="_blank" rel="noreferrer">LinkedIn ↗</a><a href="/privacy">Privacy</a><button type="button" onClick={() => openContact("message")}>Start a conversation</button></div>
        <p>© {new Date().getFullYear()} Averr Studios. Built end to end in Toronto.</p>
      </footer>

      {leadNoteOpen && <LeadCaptureNote onClose={closeLeadNote} onBook={() => chooseLeadPath("booking")} />}

      <div className={`${styles.floatingContact} film-floating-contact`}>
        {floatOpen && <div className={styles.floatingMenu}><button type="button" onClick={() => { openContact("message"); setFloatOpen(false); }}>Send a message <span>↗</span></button><button type="button" onClick={() => { openContact("booking"); setFloatOpen(false); }}>Book a call <span>↗</span></button></div>}
        <button className={styles.floatingTrigger} type="button" aria-expanded={floatOpen} onClick={() => setFloatOpen((value) => !value)}><span>{floatOpen ? "Close" : "Start a conversation"}</span><i>{floatOpen ? "×" : "+"}</i></button>
      </div>
    </>
  );
}
